import React, { useEffect, useState } from "react";
import axios from "axios";
import ipconfig from "../../Core/ipconfig";
import { decryptData } from "../../Core/util/obfuscate";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Typography,
  useTheme,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Switch,
  Divider,
  Grid,
  Paper,
} from "@mui/material";
import { toast } from "react-toastify";
import SearchBar from "../../Shared/Widgets/SearchBar";
import Spinner from "../../Shared/Feature/LoadingSpinner/Spinner";
import AdminDetail from "./AdminDetails";

export default function Admin() {
  const theme = useTheme();
  const [searchValue, setSearchValue] = useState("");
  const [currentUser, setCurrentUser] = useState<any>(null);

  useEffect(() => {
    const cached = localStorage.getItem("adminMe");
    if (cached) {
      setCurrentUser(decryptData(cached));
    }
  }, []);

  const isCurrentUserSuperAdmin = currentUser?.isSuperAdmin || currentUser?.superAdmin;
  const hasWritePermission = isCurrentUserSuperAdmin;
  const [value, setValue] = useState(0);
  const [activeIndicator, setActiveIndicator] = useState(false);
  const [allAdminData, setAllAdminData] = useState([]);
  const [searchAdminData, setSearchAdminData] = useState([]);
  const [selectedAdminData, setSelectedAdminData] = useState<any>(null);
  const [selectedCompanyValue, setSelectedCompanyValue] = useState(0);
  const [status, setStatus] = useState("A-Z");

  // Create admin states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [mobilenumber, setMobilenumber] = useState("");
  const [password, setPassword] = useState("");
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  const defaultPermission = {
    users: {
      read: false,
      write: false,
      userDetails: { read: false, write: false },
      companyDetails: { read: false },
      subscriptionDetail: { read: false, write: false },
      deleteRequests: { read: false, write: false }
    },
    companies: {
      read: false,
      write: false,
      deleteCompany: { write: false }
    },
    socketConnections: {
      read: false,
      write: false
    },
    logs: {
      read: false
    },
    subscriptions: {
      read: false,
      write: false
    },
    diagnostic: {
      read: false,
      write: false
    },
    tickets: {
      read: false,
      write: false
    },
    partners: {
      read: false,
      write: false
    },
    enquiries: {
      read: false,
      write: false
    },
    adminSettings: {
      read: false,
      write: false,
      appConfig: { read: false, write: false },
      manageAdmins: { read: false, write: false }
    }
  };

  const [permission, setPermission] = useState(defaultPermission);

  const handlePermissionChange = (path: string[], val: boolean) => {
    setPermission((prev: any) => {
      const updated = JSON.parse(JSON.stringify(prev));
      let current = updated;
      for (let i = 0; i < path.length - 1; i++) {
        if (!current[path[i]]) current[path[i]] = {};
        current = current[path[i]];
      }
      current[path[path.length - 1]] = val;
      return updated;
    });
  };

  const setAllInSection = (section: string, value: boolean) => {
    setPermission((prev: any) => {
      const updated = JSON.parse(JSON.stringify(prev));
      if (section === "users") {
        updated.users.read = value;
        updated.users.write = value;
        updated.users.userDetails.read = value;
        updated.users.userDetails.write = value;
        updated.users.companyDetails.read = value;
        updated.users.subscriptionDetail.read = value;
        updated.users.subscriptionDetail.write = value;
        updated.users.deleteRequests.read = value;
        updated.users.deleteRequests.write = value;
      } else if (section === "companies") {
        updated.companies.read = value;
        updated.companies.write = value;
        updated.companies.deleteCompany.write = value;
        updated.socketConnections.read = value;
        updated.socketConnections.write = value;
        updated.logs.read = value;
      } else if (section === "admin") {
        updated.subscriptions.read = value;
        updated.subscriptions.write = value;
        updated.diagnostic.read = value;
        updated.diagnostic.write = value;
        updated.tickets.read = value;
        updated.tickets.write = value;
      } else if (section === "settings") {
        updated.partners.read = value;
        updated.partners.write = value;
        updated.enquiries.read = value;
        updated.enquiries.write = value;
        updated.adminSettings.read = value;
        updated.adminSettings.appConfig.read = value;
        updated.adminSettings.appConfig.write = value;
        updated.adminSettings.manageAdmins.read = value;
        updated.adminSettings.manageAdmins.write = value;
      }
      return updated;
    });
  };

  const handleAddAdminSubmit = async () => {
    if (!name.trim()) {
      toast.error("Please enter a name");
      return;
    }
    if (!email.trim()) {
      toast.error("Please enter an email");
      return;
    }
    if (!password.trim()) {
      toast.error("Please enter a password");
      return;
    }

    try {
      const payload = {
        name,
        email,
        mobilenumber,
        password,
        isSuperAdmin,
        permission: isSuperAdmin ? {} : permission,
      };

      const res = await axios.post(`${ipconfig}/adminpanel/create`, payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (res.data.status) {
        toast.success("Admin created successfully!");
        setCreateModalOpen(false);
        setName("");
        setEmail("");
        setMobilenumber("");
        setPassword("");
        setIsSuperAdmin(false);
        setPermission(defaultPermission);
        handleGetAllAdminData(status);
      }
    } catch (error: any) {
      console.error(error);
      toast.error(error.response?.data?.message || "Failed to create admin");
    }
  };

  const handleSelectedAdminDataChange = (data: any) => {
    setSelectedAdminData(data);
  };

  const handleTabValueChange = (event: any, newValue: any) => {
    setValue(newValue);
  };

  const handleSearchValueChange = () => {
    setActiveIndicator(true);
    if (searchValue.length > 0) {
      const filteredResults = allAdminData?.filter((admin: any) =>
        admin?.userName?.toLowerCase().includes(searchValue.toLowerCase())
      );
      setSearchAdminData(filteredResults);
      setSelectedAdminData(filteredResults[0] || null);
      setSelectedCompanyValue(0);
    } else {
      setSearchAdminData(allAdminData);
      setSelectedAdminData(allAdminData[selectedCompanyValue] || allAdminData[0] || null);
    }
    setActiveIndicator(false);
  };

  const handleChange = (event: any) => {
    setStatus(event.target.value);
  };

  const handleGetAllAdminData = async (sortByStatus: string) => {
    try {
      setActiveIndicator(true);
      const response = await axios.get(
        `${ipconfig}/adminpanel/fetch?sortBy=${sortByStatus}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.status === 200) {
        const data = response?.data?.data || [];
        setSearchAdminData(data);
        setAllAdminData(data);
        setSelectedAdminData(data[selectedCompanyValue] || data[0] || null);
      }
      setActiveIndicator(false);
    } catch (error) {
      console.log(error, "Error fetching admins");
      setActiveIndicator(false);
    }
  };

  useEffect(() => {
    handleGetAllAdminData(status);
  }, [status]);

  useEffect(() => {
    handleSearchValueChange();
  }, [searchValue, allAdminData]);

  return (
    <Box sx={{ display: "flex", width: "100%", height: "100%", gap: "10px", p: 1 }}>
      {/* Left Side: Search Bar + Admin List */}
      <Box sx={{ width: "320px", display: "flex", flexDirection: "column", gap: "12px", flexShrink: 0 }}>
        <Box sx={{ width: "100%" }}>
          <SearchBar onSearch={setSearchValue} />
        </Box>

        <Box
          sx={{
            backgroundColor: "background.paper",
            borderRadius: "12px",
            border: "1px solid",
            borderColor: "divider",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              padding: "12px 16px",
              borderBottom: "1px solid",
              borderColor: "divider",
              gap: "10px"
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography sx={{ color: "text.primary", fontSize: "15px", fontWeight: 700 }}>
                Admins ({searchAdminData.length})
              </Typography>
              {hasWritePermission && (
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setCreateModalOpen(true)}
                  sx={{ textTransform: "none", fontWeight: 600, fontSize: "12.5px", px: 1.5 }}
                >
                  Add Admin
                </Button>
              )}
            </Box>
            <FormControl size="small" fullWidth>
              <InputLabel id="admin-sort-label" sx={{ fontSize: "12.5px" }}>Sort By</InputLabel>
              <Select
                labelId="admin-sort-label"
                value={status}
                label="Sort By"
                onChange={handleChange}
                sx={{ fontSize: "12px" }}
              >
                <MenuItem value="A-Z" sx={{ fontSize: "12px" }}>A-Z Name</MenuItem>
                <MenuItem value="Z-A" sx={{ fontSize: "12px" }}>Z-A Name</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {activeIndicator ? (
            <Spinner height="100%" />
          ) : (
            <Box 
              id="admin-list-container"
              sx={{
                flexGrow: 1,
                overflowY: "auto",
                padding: "8px",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                "&::-webkit-scrollbar": {
                  width: "5px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "divider",
                  borderRadius: "4px",
                },
              }}
            >
              {searchAdminData.map((item: any, index: any) => {
                const isSelected = selectedAdminData && selectedAdminData._id === item._id;
                return (
                  <Box
                    key={item._id || index}
                    onClick={() => {
                      handleSelectedAdminDataChange(item);
                      setSelectedCompanyValue(index);
                    }}
                    sx={{
                      padding: "12px 16px",
                      cursor: "pointer",
                      backgroundColor: isSelected
                        ? theme.palette.mode === "light" ? "#f0f7ff" : "#1e3a8a33"
                        : "background.paper",
                      border: "1px solid",
                      borderColor: isSelected ? "primary.main" : "divider",
                      borderRadius: "8px",
                      "&:hover": {
                        backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "#1e293b80",
                      },
                      transition: "all 0.2s ease",
                    }}
                  >
                    <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "text.primary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {item?.name || item?.userName}
                    </Typography>
                    {item?.role && (
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        Role: {item.role}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>

      {/* Right Side: Tab and details */}
      <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: "12px" }}>
          <Tabs value={value} onChange={handleTabValueChange}>
            <Tab label="Admin Details" sx={{ fontWeight: 600, textTransform: "none", fontSize: "14px" }} />
          </Tabs>
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            backgroundColor: "background.paper",
            borderRadius: "12px",
            border: "1px solid",
            borderColor: "divider",
            overflow: "auto",
            padding: "20px",
            height: "100%",
          }}
        >
          {value === 0 && selectedAdminData ? (
            <AdminDetail
              data={selectedAdminData}
              updatedetails={() => handleGetAllAdminData(status)}
              readOnly={!hasWritePermission}
            />
          ) : (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
              <Typography variant="body1" sx={{ color: "text.secondary", fontWeight: 500 }}>
                No admin selected.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Create Admin Dialog */}
      <Dialog
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "12px" }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Create New Admin User
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: "16px", p: "24px", maxHeight: "70vh", overflowY: "auto" }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Name"
                fullWidth
                size="small"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email Address"
                fullWidth
                size="small"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Mobile / Phone Number"
                fullWidth
                size="small"
                value={mobilenumber}
                onChange={(e) => setMobilenumber(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Password"
                fullWidth
                size="small"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 1 }} />

          <FormControlLabel
            control={
              <Switch
                checked={isSuperAdmin}
                onChange={(e) => setIsSuperAdmin(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography sx={{ fontWeight: 700, fontSize: "14px" }}>
                Super Admin (Grants access to all sections)
              </Typography>
            }
          />

          {!isSuperAdmin && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 750, color: "primary.main", mb: 1 }}>
                Configure Custom Permissions
              </Typography>

              <Grid container spacing={3}>
                {/* Users permissions */}
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: "10px" }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.primary" }}>
                        Users Management
                      </Typography>
                      <Box sx={{ display: "flex", gap: "8px" }}>
                        <Button size="small" variant="text" onClick={() => setAllInSection("users", true)} sx={{ textTransform: "none", fontSize: "11px", minWidth: 0, p: 0 }}>Allow All</Button>
                        <Typography sx={{ fontSize: "11px", color: "text.secondary" }}>|</Typography>
                        <Button size="small" variant="text" color="error" onClick={() => setAllInSection("users", false)} sx={{ textTransform: "none", fontSize: "11px", minWidth: 0, p: 0 }}>Deny All</Button>
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permission.users.read} onChange={(e) => handlePermissionChange(["users", "read"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Read User Directory</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permission.users.write} onChange={(e) => handlePermissionChange(["users", "write"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Write / Edit Users</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permission.users.userDetails.read} onChange={(e) => handlePermissionChange(["users", "userDetails", "read"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Read User Details Tab</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permission.users.userDetails.write} onChange={(e) => handlePermissionChange(["users", "userDetails", "write"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Write User Details Tab</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permission.users.companyDetails.read} onChange={(e) => handlePermissionChange(["users", "companyDetails", "read"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Read User Linked Companies</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permission.users.subscriptionDetail.read} onChange={(e) => handlePermissionChange(["users", "subscriptionDetail", "read"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Read User Subscription Details</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permission.users.subscriptionDetail.write} onChange={(e) => handlePermissionChange(["users", "subscriptionDetail", "write"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Write User Subscription Details</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permission.users.deleteRequests.read} onChange={(e) => handlePermissionChange(["users", "deleteRequests", "read"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Read Account Deletion Requests</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permission.users.deleteRequests.write} onChange={(e) => handlePermissionChange(["users", "deleteRequests", "write"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Approve Account Deletions</Typography>}
                      />
                    </Box>
                  </Paper>
                </Grid>

                {/* Companies permissions */}
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: "10px", height: "100%" }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.primary" }}>
                        Companies & Connections
                      </Typography>
                      <Box sx={{ display: "flex", gap: "8px" }}>
                        <Button size="small" variant="text" onClick={() => setAllInSection("companies", true)} sx={{ textTransform: "none", fontSize: "11px", minWidth: 0, p: 0 }}>Allow All</Button>
                        <Typography sx={{ fontSize: "11px", color: "text.secondary" }}>|</Typography>
                        <Button size="small" variant="text" color="error" onClick={() => setAllInSection("companies", false)} sx={{ textTransform: "none", fontSize: "11px", minWidth: 0, p: 0 }}>Deny All</Button>
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permission.companies.read} onChange={(e) => handlePermissionChange(["companies", "read"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Read Companies Directory</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permission.companies.write} onChange={(e) => handlePermissionChange(["companies", "write"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Write / Sync Companies</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permission.companies.deleteCompany.write} onChange={(e) => handlePermissionChange(["companies", "deleteCompany", "write"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Delete Company Node</Typography>}
                      />
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
                        Sockets & Diagnostic Logs
                      </Typography>
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permission.socketConnections.read} onChange={(e) => handlePermissionChange(["socketConnections", "read"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Read Live Sockets</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permission.socketConnections.write} onChange={(e) => handlePermissionChange(["socketConnections", "write"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Disconnect Device Sockets</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permission.logs.read} onChange={(e) => handlePermissionChange(["logs", "read"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Read System Logs Console</Typography>}
                      />
                    </Box>
                  </Paper>
                </Grid>

                {/* Subscriptions Plans, Diagnostics, Tickets, Partners */}
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: "10px", height: "100%" }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.primary" }}>
                        Administrative Modules
                      </Typography>
                      <Box sx={{ display: "flex", gap: "8px" }}>
                        <Button size="small" variant="text" onClick={() => setAllInSection("admin", true)} sx={{ textTransform: "none", fontSize: "11px", minWidth: 0, p: 0 }}>Allow All</Button>
                        <Typography sx={{ fontSize: "11px", color: "text.secondary" }}>|</Typography>
                        <Button size="small" variant="text" color="error" onClick={() => setAllInSection("admin", false)} sx={{ textTransform: "none", fontSize: "11px", minWidth: 0, p: 0 }}>Deny All</Button>
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permission.subscriptions.read} onChange={(e) => handlePermissionChange(["subscriptions", "read"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Read Global Subscriptions / Plans</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permission.subscriptions.write} onChange={(e) => handlePermissionChange(["subscriptions", "write"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Write / Create Subscription Plans</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permission.diagnostic.read} onChange={(e) => handlePermissionChange(["diagnostic", "read"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Read Diagnostics Dashboard</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permission.diagnostic.write} onChange={(e) => handlePermissionChange(["diagnostic", "write"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Execute Server Diagnostics</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permission.tickets.read} onChange={(e) => handlePermissionChange(["tickets", "read"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Read Support Tickets</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permission.tickets.write} onChange={(e) => handlePermissionChange(["tickets", "write"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Write / Reply to Tickets</Typography>}
                      />
                    </Box>
                  </Paper>
                </Grid>

                {/* Partners, Enquiries, Admin Settings */}
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: "10px", height: "100%" }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.primary" }}>
                        Partner & System Settings
                      </Typography>
                      <Box sx={{ display: "flex", gap: "8px" }}>
                        <Button size="small" variant="text" onClick={() => setAllInSection("settings", true)} sx={{ textTransform: "none", fontSize: "11px", minWidth: 0, p: 0 }}>Allow All</Button>
                        <Typography sx={{ fontSize: "11px", color: "text.secondary" }}>|</Typography>
                        <Button size="small" variant="text" color="error" onClick={() => setAllInSection("settings", false)} sx={{ textTransform: "none", fontSize: "11px", minWidth: 0, p: 0 }}>Deny All</Button>
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permission.partners.read} onChange={(e) => handlePermissionChange(["partners", "read"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Read Partner Referrals</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permission.partners.write} onChange={(e) => handlePermissionChange(["partners", "write"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Write Referral payouts / plans</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permission.enquiries.read} onChange={(e) => handlePermissionChange(["enquiries", "read"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Read Inbound Enquiries</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permission.enquiries.write} onChange={(e) => handlePermissionChange(["enquiries", "write"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Update Enquiry status</Typography>}
                      />
                      <Divider sx={{ my: 0.5 }} />
                      <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
                        App Configuration & Roles
                      </Typography>
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permission.adminSettings.read} onChange={(e) => handlePermissionChange(["adminSettings", "read"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Read System Settings</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permission.adminSettings.appConfig.read} onChange={(e) => handlePermissionChange(["adminSettings", "appConfig", "read"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Read Global System Toggles</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permission.adminSettings.appConfig.write} onChange={(e) => handlePermissionChange(["adminSettings", "appConfig", "write"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Toggle Global App Switches</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permission.adminSettings.manageAdmins.read} onChange={(e) => handlePermissionChange(["adminSettings", "manageAdmins", "read"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Read Sub-Admins Directory</Typography>}
                      />
                      <FormControlLabel
                        control={<Checkbox size="small" checked={permission.adminSettings.manageAdmins.write} onChange={(e) => handlePermissionChange(["adminSettings", "manageAdmins", "write"], e.target.checked)} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Manage Admin User & permissions</Typography>}
                      />
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: "16px 24px" }}>
          <Button onClick={() => setCreateModalOpen(false)} sx={{ textTransform: "none", fontWeight: 600 }}>
            Cancel
          </Button>
          <Button onClick={handleAddAdminSubmit} variant="contained" sx={{ textTransform: "none", fontWeight: 600, px: 3 }}>
            Create Admin
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
